import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import CachedImage from "../components/CachedImage";
import SocialIconButton from "../components/SocialIconButton";
import content from "../content.json";

const { business, serviceCatalog } = content;

export default function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesMenuAnchor, setServicesMenuAnchor] = useState(null);

  const isHome = location.pathname === "/";

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: "white.main",
        color: "dark.main",
        borderBottom: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Toolbar
        sx={{
          maxWidth: "1536px",
          width: "100%",
          mx: "auto",
          py: 1,
          px: { xs: 2, md: 3 },
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          viewTransition
          sx={{ display: "flex", alignItems: "center", gap: 1.25, textDecoration: "none", flexGrow: 1 }}
        >
          <CachedImage
            src={business.heroImage}
            alt={`${business.name} logo`}
            sx={{ height: { xs: 36, sm: 46 }, width: "auto", display: "block", flexShrink: 0 }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {business.name}
            </Typography>
            <Chip
              label={business.tagline}
              size="small"
              color="secondary"
              sx={{
                height: 20,
                alignSelf: "flex-start",
                fontSize: "0.65rem",
                fontWeight: 600,
                display: { xs: "none", sm: "flex" },
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>
        </Box>

        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              component={RouterLink}
              to="/"
              viewTransition
              variant={isHome ? "outlined" : "text"}
              color="dark"
              size="small"
            >
              Home
            </Button>

            <Button
              variant="text"
              color="dark"
              size="small"
              endIcon={<KeyboardArrowDownIcon fontSize="small" />}
              onClick={(e) => setServicesMenuAnchor(e.currentTarget)}
            >
              Services
            </Button>
            <Menu
              anchorEl={servicesMenuAnchor}
              open={Boolean(servicesMenuAnchor)}
              onClose={() => setServicesMenuAnchor(null)}
            >
              {serviceCatalog.map((service) => (
                <MenuItem
                  key={service.slug}
                  component={RouterLink}
                  to={service.route}
                  viewTransition
                  onClick={() => setServicesMenuAnchor(null)}
                >
                  {service.name}
                </MenuItem>
              ))}
            </Menu>

            <Button component="a" href={business.phoneHref} variant="text" color="dark" size="small">
              {business.phone}
            </Button>

            <Button
              component={RouterLink}
              to="/appointments"
              viewTransition
              variant="gradient"
              color="primary"
              size="small"
            >
              Book Now
            </Button>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <SocialIconButton platform="facebook" href={business.facebookUrl} businessName={business.name} />
              <SocialIconButton platform="instagram" href={business.instagramUrl} businessName={business.name} />
            </Box>
          </Box>
        )}

        {isMobile && (
          <IconButton onClick={() => setDrawerOpen(true)} aria-label="open navigation menu" color="inherit">
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, pt: 2 }} role="presentation">
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="close navigation menu">
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            <ListItemButton component={RouterLink} to="/" viewTransition onClick={() => setDrawerOpen(false)}>
              <ListItemText primary="Home" />
            </ListItemButton>
            <Typography variant="overline" color="text" sx={{ px: 2, pt: 1, display: "block" }}>
              Services
            </Typography>
            {serviceCatalog.map((service) => (
              <ListItemButton
                key={service.slug}
                component={RouterLink}
                to={service.route}
                viewTransition
                onClick={() => setDrawerOpen(false)}
                sx={{ pl: 3 }}
              >
                <ListItemText primary={service.name} />
              </ListItemButton>
            ))}
            <Box sx={{ px: 2, pt: 2 }}>
              <Button
                component={RouterLink}
                to="/appointments"
                viewTransition
                variant="gradient"
                color="primary"
                fullWidth
                onClick={() => setDrawerOpen(false)}
              >
                Book Now
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, pt: 2 }}>
              <SocialIconButton
                platform="facebook"
                href={business.facebookUrl}
                businessName={business.name}
                size="large"
              />
              <SocialIconButton
                platform="instagram"
                href={business.instagramUrl}
                businessName={business.name}
                size="large"
              />
            </Box>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
